/**
 * Roof joist sizing from TräGuiden's span table for single-span roof joists
 * (see TRAGUIDEN_ROOF_JOIST_TABLE below). The table covers a light roof
 * (0,55 kN/m²) of at most 6° pitch with no snow drifts; it does not check wind
 * uplift, joints, bearing or the complete load path. Keep verificationRequired
 * in every result until an engineered design replaces it.
 */
import { getRuleValue, type RuleOverrides } from '../../data/ruleRegistry';
import { lookupSnowZone, DEFAULT_SNOW_ZONE } from './snowZoneData';

// ─── Types ──────────────────────────────────────────────────

export interface RoofJoistInput {
    /**
     * Roof span in metres — the CLEAR distance between the load-bearing walls,
     * i.e. an interior dimension, never the building's outer width. Engine
     * callers derive it with `getRoofJoistClearSpanM` (outer width minus the
     * two stud depths); the public snow-zone calculator asks the user for the
     * same thing ("avstånd mellan bärande väggar").
     */
    spanMeters: number;
    /** Snow zone value — if absent, looked up from postalCode */
    snowZone?: number;
    /** Swedish postal code (used to determine snow zone if not explicit) */
    postalCode?: string;
}

export interface RoofJoistResult {
    /** This preliminary lookup is not an engineered load-path/connection check. */
    verificationRequired?: boolean;
    /** Recommended joist dimension string, e.g. "45×195 mm" */
    dimension: string;
    /** Joist depth in mm, e.g. 195 */
    depthMm: number;
    /** Center-to-center spacing in mm */
    ccSpacingMm: number;
    /** The resolved snow zone */
    snowZone: number;
    /** Region name if resolved from postal code */
    region: string | null;
    /** Warning text (null if none) */
    warning: string | null;
    /**
     * Longest clear span the chosen section carries per the span table, in m.
     * Absent when no conventional section fits.
     */
    tableSpanM?: number;
    /**
     * Set by `getStructuralRoofJoists` only: the depth is the span table's
     * deepest section, drawn in place of a glulam beam nobody has sized.
     */
    glulamPlaceholder?: boolean;
    /** Error message if span exceeds conventional limits */
    error: string | null;
    /** Optional config-aware span direction used by engine-level roof sizing. */
    spanDirection?: 'width' | 'length';
    /** Optional resolved span after entry-wall/glazing rules. */
    spanMeters?: number;
    /** Optional bearing walls for the resolved span. */
    bearingWalls?: Array<'front' | 'back' | 'left' | 'right'>;
    /** True when a panorama wall is also a roof-bearing wall and needs bracing review. */
    requiresLateralBracing?: boolean;
    /** Human-readable warning for bracing review when applicable. */
    lateralBracingWarning?: string | null;
}

// ─── Span table ─────────────────────────────────────────────

/**
 * TräGuiden, "Takbjälkar av konstruktionsvirke i ett fack" (updated
 * 2022-02-18):
 * https://www.traguiden.se/konstruktion/dimensionering/hjalpmedel---tabeller/tak/takbalkar-av-konstruktionsvirke-i-ett-fack/
 *
 * Maximum clear length (fri längd, m) of C24 roof joists at c/c 1 200 mm,
 * light roof 0,55 kN/m², pitch ≤ 6°, EKS 12, safety class 2, climate class 2,
 * deflection ≤ min(20 mm, L/300). Columns are the snow zones below. Copied
 * as printed; the c/c 600 factor is applied at lookup.
 */
export const TRAGUIDEN_ROOF_JOIST_SNOW_ZONES = [1.0, 1.5, 2.0, 2.5, 3.0, 3.5] as const;

export const TRAGUIDEN_ROOF_JOIST_TABLE: ReadonlyArray<{
    depthMm: number;
    spansAtCc1200M: readonly number[];
}> = [
    { depthMm: 145, spansAtCc1200M: [2.39, 2.22, 2.06, 1.95, 1.87, 1.79] },
    { depthMm: 170, spansAtCc1200M: [2.81, 2.61, 2.42, 2.29, 2.19, 2.09] },
    { depthMm: 195, spansAtCc1200M: [3.22, 2.99, 2.77, 2.63, 2.51, 2.40] },
    { depthMm: 220, spansAtCc1200M: [3.63, 3.37, 3.13, 2.97, 2.84, 2.71] },
];

/** TräGuiden: "Vid centrumavstånd (C) 600 mm … multipliceras med 1,19." */
export const TRAGUIDEN_CC600_FACTOR = 1.19;

/**
 * Longest clear span a joist of `depthMm` carries in `snowZone` at
 * `ccSpacingMm`, or null when the table does not cover the case. A zone
 * between two columns reads the next higher one; a spacing between 600 and
 * 1 200 mm reads the 1 200 column. Both round toward the safe side.
 */
export function roofJoistTableSpanM(
    depthMm: number,
    snowZone: number,
    ccSpacingMm: number,
): number | null {
    const row = TRAGUIDEN_ROOF_JOIST_TABLE.find((entry) => entry.depthMm === depthMm);
    const column = TRAGUIDEN_ROOF_JOIST_SNOW_ZONES.findIndex((zone) => zone >= snowZone - 1e-9);
    if (!row || column < 0 || ccSpacingMm > 1200) return null;
    const factor = ccSpacingMm <= 600 ? TRAGUIDEN_CC600_FACTOR : 1;
    return row.spansAtCc1200M[column] * factor;
}

const formatM = (value: number) => value.toFixed(2);

// ─── Public API ─────────────────────────────────────────────

/**
 * Size C24 roof joists for the given snow zone and clear span: the smallest
 * section of the span table that carries the span.
 */
export function calculateRoofJoists(
    input: RoofJoistInput,
    overrides: RuleOverrides = {},
): RoofJoistResult {
    const ccSpacing = getRuleValue<number>('snowzone.ccSpacingMm', overrides);
    const maxSpan = getRuleValue<number>('snowzone.maxConventionalSpanM', overrides);

    // Resolve snow zone
    let snowZone = input.snowZone ?? DEFAULT_SNOW_ZONE;
    let region: string | null = null;

    if (input.snowZone === undefined && input.postalCode) {
        const lookup = lookupSnowZone(input.postalCode);
        if (lookup) {
            snowZone = lookup.zone;
            region = lookup.region;
        }
    }

    const span = input.spanMeters;
    const glulam = (error: string, dimension = 'Limträbalk krävs'): RoofJoistResult => ({
        dimension,
        verificationRequired: true,
        depthMm: 0,
        ccSpacingMm: ccSpacing,
        snowZone,
        region,
        warning: null,
        error,
    });

    // Admin cap on conventional timber, whatever the table would allow.
    if (span > maxSpan) {
        return glulam(
            `Spännvidden ${span.toFixed(1)} m överstiger ${maxSpan} m. Vanligt konstruktionsvirke (C24) räcker ej — limträbalk krävs. Anlita konstruktör.`,
        );
    }

    const largest = TRAGUIDEN_ROOF_JOIST_TABLE[TRAGUIDEN_ROOF_JOIST_TABLE.length - 1];
    if (roofJoistTableSpanM(largest.depthMm, snowZone, ccSpacing) === null) {
        return glulam(
            `Snözon ${snowZone.toFixed(1)} med c/c ${ccSpacing} mm ligger utanför spännviddstabellen (snözon 1,0–3,5, c/c högst 1 200 mm). Takbjälkarna måste dimensioneras av konstruktör.`,
            'Konstruktör krävs',
        );
    }

    // Smallest section whose tabulated clear length covers the span.
    const fit = TRAGUIDEN_ROOF_JOIST_TABLE.map((entry) => ({
        depthMm: entry.depthMm,
        tableSpanM: roofJoistTableSpanM(entry.depthMm, snowZone, ccSpacing) ?? 0,
    })).find((entry) => span <= entry.tableSpanM + 1e-9);

    if (!fit) {
        const largestSpanM = roofJoistTableSpanM(largest.depthMm, snowZone, ccSpacing) ?? 0;
        return glulam(
            `Spännvidden ${span.toFixed(1)} m överstiger ${formatM(largestSpanM)} m, vad 45×${largest.depthMm} C24 c/c ${ccSpacing} klarar i snözon ${snowZone.toFixed(1)}. Vanligt konstruktionsvirke räcker ej — limträbalk krävs. Anlita konstruktör.`,
        );
    }
    const depthMm = fit.depthMm;

    // Generate warning for high snow zones
    const warning = snowZone >= 3.5
        ? `⚠️ Hög snözon (${snowZone})! Kontrollera att takbjälkarna monteras enligt konstruktionsritning. Överväg att anlita konstruktör för verifiering.`
        : null;

    return {
        dimension: `45×${depthMm} mm`,
        verificationRequired:true,
        depthMm,
        ccSpacingMm: ccSpacing,
        snowZone,
        region,
        warning,
        error: null,
        tableSpanM: Number(fit.tableSpanM.toFixed(2)),
    };
}
